import React, { useState, useRef, useEffect } from 'react';
import { Check, Star, Zap, ArrowRight, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { pricingData } from '../data/mock';

export const Pricing = () => {
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-40 h-40 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '1s'
        }}></div>
        <div className="absolute bottom-32 right-16 w-32 h-32 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '3s'
        }}></div>
        
        {/* Price symbols floating */}
        <div className="absolute top-40 right-32 animate-float opacity-20" style={{ animationDelay: '2s' }}>
          <DollarSign className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
        </div>
        <div className="absolute bottom-40 left-32 animate-float opacity-15" style={{ animationDelay: '4s' }}>
          <DollarSign className="w-6 h-6" style={{ color: 'var(--accent-purple)' }} />
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <DollarSign className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Pricing Plans
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Choose Your Automation Journey
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Flexible pricing plans designed to grow with your business. Start small and scale up as you automate more processes.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {pricingData.map((plan, index) => {
            const isHovered = hoveredPlan === index;
            const isPopular = plan.popular;
            
            return (
              <div
                key={plan.id}
                className={`group relative transition-all duration-1000 delay-${index * 200} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isPopular ? 'lg:-mt-4' : ''}`}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="flex items-center space-x-2 px-4 py-2 rounded-full animate-pulse-glow" style={{
                      background: 'var(--accent-primary)',
                      color: 'var(--bg-primary)'
                    }}>
                      <Star className="w-4 h-4" />
                      <span className="body-sm font-medium">Most Popular</span>
                    </div>
                  </div>
                )}

                {/* Pricing Card */}
                <div className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  isHovered ? 'scale-105' : ''
                } ${isPopular ? 'ring-2' : ''}`} style={{
                  background: 'var(--bg-secondary)',
                  border: `2px solid ${isHovered || isPopular ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  ringColor: isPopular ? 'var(--accent-primary)' : 'transparent'
                }}>
                  {/* Animated Background for Popular */}
                  {isPopular && (
                    <div className="absolute inset-0 rounded-2xl opacity-5" style={{
                      background: `linear-gradient(135deg, var(--accent-primary), var(--accent-purple))`
                    }}></div>
                  )}

                  <div className="relative z-10">
                    {/* Plan Header */}
                    <div className="text-center mb-8">
                      <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                        {plan.name}
                      </h3>
                      
                      {/* Price */}
                      <div className="mb-4">
                        <span className="display-sm" style={{ color: 'var(--accent-primary)' }}>
                          {plan.price}
                        </span>
                        {plan.period && (
                          <span className="body-md ml-2" style={{ color: 'var(--text-muted)' }}>
                            {plan.period}
                          </span>
                        )}
                      </div>
                      
                      <p className="body-md" style={{ color: 'var(--text-secondary)' }}>
                        {plan.description}
                      </p>
                    </div>

                    {/* Features List */}
                    <div className="mb-8">
                      <ul className="space-y-4">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 transition-all duration-300 ${
                              isHovered ? 'animate-pulse-glow' : ''
                            }`} style={{
                              background: 'var(--accent-primary)'
                            }}>
                              <Check className="w-3 h-3" style={{ color: 'var(--bg-primary)' }} />
                            </div>
                            <span className="body-md group-hover:text-white transition-colors duration-300" 
                                  style={{ color: 'var(--text-secondary)' }}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    <Button 
                      className={`w-full group ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => scrollToSection('#contact')}
                    >
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    {/* Hover Effect Lines */}
                    <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl transition-all duration-300 ${
                      isHovered ? 'opacity-100' : 'opacity-0'
                    }`} style={{
                      background: `linear-gradient(90deg, var(--accent-primary), var(--accent-purple))`
                    }}></div>
                  </div>
                </div>

                {/* Floating Elements */}
                {isHovered && (
                  <div className="absolute -inset-4 pointer-events-none">
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full animate-float" style={{
                      background: 'var(--accent-primary)',
                      animationDelay: '0.5s'
                    }}></div>
                    <div className="absolute bottom-2 left-2 w-1 h-1 rounded-full animate-float" style={{
                      background: 'var(--accent-purple)',
                      animationDelay: '1s'
                    }}></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className={`text-center mb-16 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "30-Day Free Trial",
                description: "Try all features risk-free for 30 days"
              },
              {
                icon: Check,
                title: "No Setup Fees",
                description: "Get started immediately with zero upfront costs"
              },
              {
                icon: Star,
                title: "24/7 Support",
                description: "Expert support whenever you need assistance"
              }
            ].map((info, index) => {
              const IconComponent = info.icon;
              return (
                <div key={index} className="p-6 rounded-2xl" style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}>
                  <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{
                    background: 'var(--accent-bg)'
                  }}>
                    <IconComponent className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h4 className="h4 mb-2" style={{ color: 'var(--text-primary)' }}>
                    {info.title}
                  </h4>
                  <p className="body-sm" style={{ color: 'var(--text-secondary)' }}>
                    {info.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className={`text-center transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-3xl mx-auto p-8 rounded-2xl relative overflow-hidden" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            {/* Background Animation */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-20 h-20 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-primary), transparent)`,
                animationDelay: '1s'
              }}></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-purple), transparent)`,
                animationDelay: '2s'
              }}></div>
            </div>

            <div className="relative z-10">
              <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                Need a Custom Enterprise Solution?
              </h3>
              <p className="body-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Contact our sales team to discuss volume discounts, custom features, 
                and enterprise-grade security options tailored to your organization.
              </p>
              <Button 
                className="btn-primary animate-pulse-glow"
                onClick={() => scrollToSection('#contact')}
              >
                Contact Sales Team
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};